import { Popover, Transition } from '@headlessui/react'
import { ChevronRightIcon, PlusIcon } from '@heroicons/react/outline'
import { CalendarEventItem } from 'components/calendarEventItem'
import React, { Fragment } from 'react'
import { CalendarEvent } from 'utils'

type Props = {
	day: Array<CalendarEvent>
	maxQuantityElement: number
	position?: 'absolute' | 'fixed'
	children?: JSX.Element
	typeView: 'month' | 'week'
	placement?: 'top' | 'bottom' | 'left' | 'right' | 'top-left' | 'top-right'
}

export const CalendarPopover: React.FC<Props> = ({
	day,
	maxQuantityElement,
	position = 'fixed',
	typeView,
	placement,
	children
}) => {
	const remainingEventsCount = day.length - maxQuantityElement

	const childrenDefault = (): JSX.Element => {
		return (
			<div className="flex items-center justify-between w-full h-full gap-2 px-3 py-1.5 rounded-lg border bg-[#E8ECEE] border-[#CFDADE]">
				<div className="flex items-center gap-1.5">
					<PlusIcon className="w-4 h-4 flex-shrink-0 text-[#668593]" />
					<span className="text-sm font-medium text-[#474B51]">
						{remainingEventsCount} View more
					</span>
				</div>
				<ChevronRightIcon className="w-4 h-4 flex-shrink-0 text-[#668593]" />
			</div>
		)
	}

	const getPopoverPositionClass = (placementProps?: string): string => {
		switch (placementProps) {
			case 'top':
				return 'bottom-full mb-2'
			case 'left':
				return 'right-full mr-2'
			case 'right':
				return 'left-full ml-2'
			case 'top-left':
				return 'bottom-full right-full mb-2 mr-2'
			case 'top-right':
				return 'bottom-full left-full mb-2 ml-2'
			case 'bottom':
			default:
				return 'top-full mt-2'
		}
	}

	return (
		<Popover className={`relative ${typeView === 'week' ? 'h-full' : ''}`}>
			{() => (
				<>
					<Popover.Button
						className={`flex w-full ${
							typeView === 'week' ? 'h-full' : ''
						} focus:outline-none`}
					>
						{children ?? childrenDefault()}
					</Popover.Button>
					<Transition
						as={Fragment}
						enter="transition ease-out duration-200"
						enterFrom="opacity-0 translate-y-1"
						enterTo="opacity-100 translate-y-0"
						leave="transition ease-in duration-150"
						leaveFrom="opacity-100 translate-y-0"
						leaveTo="opacity-0 translate-y-1"
					>
						<Popover.Panel
							className={`${position} ${
								typeView === 'month'
									? 'z-30 top-full mt-1 left-0 w-full'
									: `z-40 transform ${getPopoverPositionClass(placement)}`
							}`}
						>
							<div className="overflow-hidden rounded-lg shadow-xl ring-1 ring-gray-19">
								<div className="flex flex-col gap-1 bg-white p-1 max-h-[170px] overflow-y-auto">
									<ol className="flex flex-col gap-1">
										{day
											.slice(maxQuantityElement)
											.sort(
												(a, b) =>
													new Date(String(a.publish_date)).getTime() -
													new Date(String(b.publish_date)).getTime()
											)
											.map(event => (
												<CalendarEventItem
													key={`${event.id}-${event.status}`}
													quantityElement={4}
													event={event}
												/>
											))}
									</ol>
								</div>
							</div>
						</Popover.Panel>
					</Transition>
				</>
			)}
		</Popover>
	)
}

CalendarPopover.defaultProps = {
	position: 'fixed',
	children: undefined,
	placement: undefined
}
